import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { Button, Skeleton, Pagination } from '@/components/ui';
import { ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS, labelOf } from '@/lib/enumLabels';
import { formatMoney } from '@/lib/domain/money';
import { useUserOrdersQuery } from '../hooks/useUserOrdersQuery';

interface UserOrdersPanelProps {
  /** 회원 ID — 주문 이력은 회원 ID 로 조회한다(이름 검색 아님). */
  userId: string;
}

const PAGE_SIZE = 10;

export const UserOrdersPanel = ({ userId }: UserOrdersPanelProps) => {
  const { t } = useTranslation('common');
  const [page, setPage] = useState(1);
  const query = useUserOrdersQuery(userId, page, PAGE_SIZE);
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  if (query.isPending) {
    return (
      <div className="space-y-2" role="status" aria-live="polite">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[44px] w-full" />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-2 rounded-xl py-10 text-center text-sm"
        style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
      >
        <p>{t('users.orders.loadFailed')}</p>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          {t('users.actions.retry')}
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="py-10 text-center text-sm" style={{ color: 'var(--content-muted)' }}>
        {t('users.orders.empty')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div
        className="overflow-hidden rounded-xl"
        style={{ border: '1px solid var(--divider-strong)' }}
      >
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'var(--surface-1)', borderBottom: '1px solid var(--divider-strong)' }}>
              {(['orders.cols.orderNo', 'orders.cols.status', 'orders.cols.payment', 'orders.cols.amount', 'orders.cols.createdAt'] as const).map((key) => (
                <th
                  key={key}
                  className="px-4 py-3 text-left text-xs font-semibold"
                  style={{ color: 'var(--content-muted)' }}
                >
                  {t(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((order) => (
              <tr
                key={order.orderId}
                style={{ borderBottom: '1px solid var(--divider-soft)' }}
              >
                <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--content-strong)' }}>
                  <Link to={`/orders/${order.orderId}`} className="underline-offset-2 hover:underline" data-testid="user-order-link">
                    {order.orderNo}
                  </Link>
                </td>
                <td className="px-4 py-3" style={{ color: 'var(--content-default)' }}>
                  {labelOf(ORDER_STATUS_LABELS, order.orderStatus)}
                </td>
                <td className="px-4 py-3" style={{ color: 'var(--content-default)' }}>
                  {labelOf(PAYMENT_STATUS_LABELS, order.paymentStatus)}
                </td>
                <td className="px-4 py-3 font-medium" style={{ color: 'var(--content-strong)' }}>
                  {order.amount ? formatMoney(order.amount) : '—'}
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: 'var(--content-muted)' }}>
                  {order.orderedAt ? new Date(order.orderedAt).toLocaleDateString('ko-KR') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        total={total}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        className="mt-2"
      />
    </div>
  );
};
